import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const categoryApi = createApi({
  reducerPath: REDUCER_PATHS.CATEGORY,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    // levels
    levelsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.LEVELS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addLevels: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_LEVELS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    changeLevels: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHNAGE_LEVELS,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteLevels: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_LEVELS,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editLevelsDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_LEVELS + "/" + data?.id,
        body: {
          level: data.level,
          status: data?.status,
          pointFrom: data.pointFrom,
          newPointTo: data.newPointTo,
          oldPointTo: data?.oldPointTo,
          levelNumber: data.levelNumber,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getLevelsPoint: builder.mutation({
      query: () => ({
        url: apiConfig.GET_LAVELS_POINTS,
        method: "GET",
      }),
    }),
    // superLevels
    getSuperLevelsPoint: builder.mutation({
      query: () => ({
        url: apiConfig.GET_SUPER_LEVELS_POINTS,
        method: "GET",
      }),
    }),
    superLevelsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.SUPER_LEVELS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addSuperLevels: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_SUPER_LEVELS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    changeSuperLevels: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHNAGE_SUPER_LEVELS,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteSuperLevels: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_SUPER_LEVELS,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editSuperLevelsDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_SUPER_LEVELS + "/" + data?.id,
        body: {
          superLevel: data.superLevel,
          status: data?.status,
          pointFrom: data.pointFrom,
          newPointTo: data.newPointTo,
          oldPointTo: data?.oldPointTo,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    // Actions
    actionsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.ACTIONS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    clubAdminActionsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.CLUB_ADMIN_ACTION_LISTING,
        method: "POST",
        body: data,
      }),
    }),
    addActions: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_ACTIONS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addClubAdminActions: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_CLUB_POINTS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    actionPointsDropdown: builder.mutation({
      query: (data) => ({
        url: apiConfig.ACTION_POINTS_DROPDOWN + data.pointsType,
        method: "GET",
      }),
    }),

    changeActions: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHNAGE_ACTIONS,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteActions: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_ACTIONS,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editActionsDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_ACTIONS + "/" + data?.id,
        body: {
          title: data.title,
          slug: data.slug,
          points: data?.points,
          description: data?.description,
          drinkTypeId: data?.drinkTypeId,
          associatedWith: data?.associatedWith,
          status: data?.status,
          defaultApply: data?.defaultApply,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editClubActionsPoints: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_CLUB_POINTS + data?.id,
        body: {
          title: data?.title,
          slug: data?.slug,
          points: data?.points,
          description: data?.description,
          status: data?.status,
          defaultApply: data?.defaultApply,
          createdForClubId: data?.createdForClubId,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    // benefits
    benefitsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.BENEFIT_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_BENEFIT,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    changeBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHNAGE_BENEFIT,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_BENEFIT,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editBenefitsDetails: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${apiConfig.EDIT_BENEFIT}/${id}`, // Use the id in the URL
        method: "PUT",
        body: data, // Send only the rest of the data in the body (no id)
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    benefitsFileUplaod: builder.mutation({
      query: (data) => ({
        url: apiConfig.UPLOAD_BENEFIT_FILE,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    // clubs benefits
    clubAdminBenefitsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.CLUB_ADMIN_BENEFITS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    clubAdminAddBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.CLUB_ADMINS_BENEFITS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    clubAdminChangeBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.CLUB_ADMINS_BENEFITS,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    clubAdminDeleteBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.CLUB_ADMINS_BENEFITS,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    clubAdminEditBenefitsDetails: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${apiConfig.CLUB_ADMINS_BENEFITS}/${id}`,
        body: data,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    clubAdminBenefitsFileUplaod: builder.mutation({
      query: (data) => ({
        url: apiConfig.CLUB_ADMIN_UPLOAD_BENEFIT_FILE,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    //brand Benefits
    brandAdminBenefitsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRANDS_ADMIN_BENEFITS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    brandAdminAddBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRANDS_ADMINS_BENEFITS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    brandAdminChangeBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRANDS_ADMINS_BENEFITS,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    brandAdminDeleteBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRANDS_ADMINS_BENEFITS,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    brandAdminEditBenefitsDetails: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${apiConfig.BRANDS_ADMINS_BENEFITS}/${id}`,
        body: data,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    brandAdminBenefitsFileUplaod: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRANDS_ADMIN_UPLOAD_BENEFIT_FILE,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    workerChangeBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.WORKER_CHANGE_BENEFITS,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    //quetype
    quetypeListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.QUE_TYPE_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addQuetype: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_QUE_TYPE,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    changeQuetype: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHNAGE_QUE_TYPE,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteQuetype: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_QUE_TYPE,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editQuetypeDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_QUE_TYPE + "/" + data?.id,
        body: {
          title: data.title,
          icon: data.icon,
          status: data?.status,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    queTypeFileUplaod: builder.mutation({
      query: (data) => ({
        url: apiConfig.UPLOAD_QUE_TYPE_FILE,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    //atmosphare
    atmosphareRangesListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.ATMOSPHARE_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addAtmosphareRanges: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_ATMOSPHARE,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    changeAtmosphareRanges: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHNAGE_ATMOSPHARE,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteAtmosphareRanges: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_ATMOSPHARE,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editAtmosphareRangesDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_ATMOSPHARE + "/" + data?.id,
        body: {
          title: data?.title,
          color: data?.color,
          status: data?.status,
          icon: data?.icon,
          oldToPercentage: data?.oldToPercentage,
          toPercentage: data?.toPercentage,
          fromPercentage: data?.fromPercentage,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    fileUploadAtmosphareRanges: builder.mutation({
      query: (data) => ({
        url: apiConfig.UPLOAD_ATMOSPHARE_FILE,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getLatestAtmosphare: builder.mutation({
      query: () => ({
        url: apiConfig.GET_LATEST_ATMOSPHARE,
        method: "GET",
      }),
    }),
    // gender
    genderListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.GENDER_LIST,
        method: "POST",
        body: data,
      }),
    }),
    editGenderDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_GENDER + "/" + data?.id,
        body: {
          title: data.title,
          icon: data.icon,
          status: data?.status,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    genderFileUplaod: builder.mutation({
      query: (data) => ({
        url: apiConfig.GENDER_FILE_UPLOAD,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
  }),
});

export const {
  useGenderListingMutation,
  useGenderFileUplaodMutation,
  useEditGenderDetailsMutation,

  useAddLevelsMutation,
  useChangeLevelsMutation,
  useDeleteLevelsMutation,
  useEditLevelsDetailsMutation,
  useLevelsListingMutation,

  useAddSuperLevelsMutation,
  useChangeSuperLevelsMutation,
  useEditSuperLevelsDetailsMutation,
  useSuperLevelsListingMutation,
  useDeleteSuperLevelsMutation,

  useAddActionsMutation,
  useChangeActionsMutation,
  useDeleteActionsMutation,
  useEditActionsDetailsMutation,
  useActionsListingMutation,
  useActionPointsDropdownMutation,
  useAddClubAdminActionsMutation,
  useEditClubActionsPointsMutation,
  useClubAdminActionsListingMutation,

  useAddBenefitsMutation,
  useChangeBenefitsMutation,
  useDeleteBenefitsMutation,
  useEditBenefitsDetailsMutation,
  useBenefitsListingMutation,
  useBenefitsFileUplaodMutation,

  useAddQuetypeMutation,
  useChangeQuetypeMutation,
  useDeleteQuetypeMutation,
  useEditQuetypeDetailsMutation,
  useQuetypeListingMutation,
  useQueTypeFileUplaodMutation,

  useAtmosphareRangesListingMutation,
  useAddAtmosphareRangesMutation,
  useDeleteAtmosphareRangesMutation,
  useEditAtmosphareRangesDetailsMutation,
  useChangeAtmosphareRangesMutation,
  useFileUploadAtmosphareRangesMutation,
  useGetLatestAtmosphareMutation,

  useGetLevelsPointMutation,
  useGetSuperLevelsPointMutation,

  //club admin benefits
  useClubAdminBenefitsListingMutation,
  useClubAdminAddBenefitsMutation,
  useClubAdminChangeBenefitsMutation,
  useClubAdminBenefitsFileUplaodMutation,
  useClubAdminDeleteBenefitsMutation,
  useClubAdminEditBenefitsDetailsMutation,

  // brand admin benefits
  useBrandAdminBenefitsListingMutation,
  useBrandAdminAddBenefitsMutation,
  useBrandAdminChangeBenefitsMutation,
  useBrandAdminBenefitsFileUplaodMutation,
  useBrandAdminDeleteBenefitsMutation,
  useBrandAdminEditBenefitsDetailsMutation,
  useWorkerChangeBenefitsMutation,
} = categoryApi;

export default categoryApi;
